import type { Squad } from '@fantasy/types';
import type { LivePlayerPoints } from '../api.ts';

/** The signed-in user's approximate gameweek total, chip-aware but with no
 * auto-substitution logic — a starter who didn't play still contributes 0,
 * same as the raw per-player numbers `SquadView` already shows, and
 * replicating FPL's auto-sub logic is out of scope here.
 *
 * `activeChip` changes two independent things: Bench Boost includes all
 * fifteen players instead of just the starting XI, and Triple Captain scores
 * the captain at 3x instead of 2x. Either, both or neither can apply — they
 * aren't mutually exclusive switches. Wildcard and Free Hit don't change how
 * points are scored, so they fall through to the ordinary XI-only, 2x-captain
 * behaviour, same as no chip being active at all.
 *
 * `null` until live points have actually been fetched, so there's nothing to
 * show before the first fetch resolves. Shared by `Tracker` (the "Your squad
 * (est.)" chart line) and `SquadContextBar` (the always-visible gameweek
 * total) rather than each computing it separately. */
export function estimateSquadPoints(squad: Squad, livePoints: Map<number, LivePlayerPoints> | null): number | null {
  if (!livePoints) return null;

  const isBenchBoost = squad.activeChip === 'bboost';
  const isTripleCaptain = squad.activeChip === '3xc';
  const captainMultiplier = isTripleCaptain ? 3 : 2;

  return squad.players
    .filter((player) => isBenchBoost || player.isStarting)
    .reduce((total, player) => {
      const points = player.fplId !== undefined ? (livePoints.get(player.fplId)?.points ?? 0) : 0;
      return total + (player.isCaptain ? points * captainMultiplier : points);
    }, 0);
}
